import { Download, Eye, FileSearch, X } from "lucide-react";
import * as React from "react";

import {
  type AuditDetail,
  type AuditFilterParams,
  type AuditLogItem,
  auditApi,
} from "@/api/audit";
import { AuditDiffViewer } from "@/components/admin/AuditDiffViewer";
import { AuditFilters } from "@/components/admin/AuditFilters";
import { EmptySearch } from "@/components/illustrations/EmptySearch";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { LoadingTable } from "@/components/ui/loading-table";
import { useAuthStore } from "@/stores/auth";
import { useUIStore } from "@/stores/ui";

export function AuditLogsPage(): JSX.Element {
  const pushToast = useUIStore((s) => s.pushToast);
  const accessToken = useAuthStore((s) => s.accessToken);

  const [filter, setFilter] = React.useState<AuditFilterParams>({
    days_back: 30,
    limit: 100,
  });
  const [items, setItems] = React.useState<AuditLogItem[]>([]);
  const [hasMore, setHasMore] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [detail, setDetail] = React.useState<AuditDetail | null>(null);
  const [downloading, setDownloading] = React.useState(false);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await auditApi.list(filter);
      setItems(res.items);
      setHasMore(res.has_more);
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Lỗi tải logs",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setLoading(false);
    }
  }, [filter, pushToast]);

  React.useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDetail = async (id: string): Promise<void> => {
    try {
      const d = await auditApi.get(id);
      setDetail(d);
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Lỗi tải chi tiết",
        description: e instanceof Error ? e.message : String(e),
      });
    }
  };

  const handleExport = async (): Promise<void> => {
    setDownloading(true);
    try {
      await auditApi.downloadCsv(filter, accessToken);
      pushToast({ variant: "success", title: "CSV đã tải xuống" });
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Export thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col">
      <header className="h-12 shrink-0 flex items-center px-6 gap-3 border-b bg-background/50">
        <h1 className="font-semibold flex items-center gap-2">
          <FileSearch className="size-4" /> Audit Logs
        </h1>
        <Button
          size="sm"
          variant="outline"
          className="ml-auto"
          onClick={() => void handleExport()}
          disabled={downloading || items.length === 0}
        >
          <Download className="size-4 mr-1" />
          {downloading ? "..." : "Export CSV"}
        </Button>
      </header>

      <main className="flex-1 max-w-6xl w-full mx-auto p-6 space-y-4 overflow-y-auto">
        <AuditFilters
          value={filter}
          onChange={setFilter}
          onApply={() => void refresh()}
        />

        {loading ? (
          <LoadingTable rows={6} cols={5} />
        ) : items.length === 0 ? (
          <Card>
            <CardContent className="p-6">
              <EmptyState
                hero={<EmptySearch size={120} />}
                title="Không có audit log khớp filter"
                description="Thử mở rộng khoảng thời gian hoặc bỏ filter để xem nhiều log hơn."
                hint="Audit log lưu mọi thay đổi config quan trọng (form, workflow, role) để truy vết."
              />
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/30">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Time</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Action</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Target</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">User</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.id} className="border-b last:border-b-0 hover:bg-muted/30">
                      <td className="px-3 py-2 text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                        {new Date(it.created_at).toLocaleString("vi-VN")}
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className="text-[10px]">
                          {it.action}
                        </Badge>
                      </td>
                      <td className="px-3 py-2">
                        <code className="text-xs">{it.target_model}</code>
                        {it.target_id ? (
                          <span className="text-[10px] text-muted-foreground ml-1">
                            #{it.target_id.slice(0, 8)}
                          </span>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 text-xs font-mono">
                        {it.user_id.slice(0, 8)}...
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="size-7"
                          onClick={() => void openDetail(it.id)}
                        >
                          <Eye className="size-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {hasMore ? (
                <div className="p-2 text-center text-[11px] text-muted-foreground">
                  Còn nữa — tăng <code>limit</code> hoặc thu hẹp filter.
                </div>
              ) : null}
            </CardContent>
          </Card>
        )}
      </main>

      {detail ? (
        <AuditDetailModal detail={detail} onClose={() => setDetail(null)} />
      ) : null}
    </div>
  );
}

function AuditDetailModal({
  detail,
  onClose,
}: {
  detail: AuditDetail;
  onClose: () => void;
}): JSX.Element {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-background rounded-lg border shadow-lg max-w-5xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-4 py-3 border-b flex items-center justify-between sticky top-0 bg-background z-10">
          <div>
            <h2 className="font-semibold flex items-center gap-2">
              <Badge variant="outline">{detail.action}</Badge>
              <code className="text-xs">{detail.target_model}</code>
              {detail.target_id ? (
                <code className="text-xs text-muted-foreground">
                  #{detail.target_id}
                </code>
              ) : null}
            </h2>
            <div className="text-[11px] text-muted-foreground mt-1">
              {new Date(detail.created_at).toLocaleString("vi-VN")} ·
              user <code>{detail.user_id.slice(0, 8)}...</code>
              {detail.ip_address ? (
                <>
                  {" · "}
                  IP {detail.ip_address}
                </>
              ) : null}
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="size-4" />
          </button>
        </header>
        <div className="p-4">
          <AuditDiffViewer
            before={detail.before_state}
            after={detail.after_state}
            layout="side"
          />
        </div>
      </div>
    </div>
  );
}
